'use client'

import { useEffect, useEffectEvent, useState } from 'react'

/**
 * Tope del bloqueo tras un 429. Un `Retry-After` de un día (la ventana diaria
 * abre a las 00:00 UTC) no necesita un temporizador de un día: una vista de
 * página rara vez dura más de una hora, y si dura, el servidor vuelve a decir 429.
 */
export const MAX_RETRY_LOCK_MS = 60 * 60 * 1000

/**
 * Segundos de la cabecera `Retry-After` de la respuesta, o 0 si no viene o no
 * es un número. No confundir con `retryAfterSecondsOf` del dominio, que los
 * calcula en el servidor a partir del fin de la ventana.
 */
export function retryAfterHeaderSeconds(response: Response): number {
  const seconds = Number(response.headers.get('Retry-After'))
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0
}

/**
 * Tras un 429 el botón sigue deshabilitado durante el `Retry-After`: un clic
 * más sólo gasta cupo y alarga la espera. `lockFor(segundos)` lo arranca y el
 * propio hook lo suelta, con el temporizador limpiado si el componente se va.
 */
export function useRetryLock(): { locked: boolean; lockFor: (seconds: number) => void } {
  const [lockMs, setLockMs] = useState(0)

  useEffect(() => {
    if (lockMs === 0) return
    const timer = setTimeout(() => setLockMs(0), lockMs)
    return () => clearTimeout(timer)
  }, [lockMs])

  const lockFor = (seconds: number) => setLockMs(Math.min(seconds * 1000, MAX_RETRY_LOCK_MS))
  return { locked: lockMs > 0, lockFor }
}

/**
 * Atrás desde el checkout de Stripe restaura la página del bfcache con el
 * estado de React tal y como se quedó —«Un momento…», deshabilitado—, sin
 * volver a montar nada. `pageshow` con `persisted` es la única señal de eso.
 *
 * `useEffectEvent`: el listener se suscribe una sola vez y siempre llama a la
 * última `onRestore`, así que el llamante puede pasar una lambda inline sin que
 * cada render quite y vuelva a poner el `pageshow`.
 */
export function useOnBfcacheRestore(onRestore: () => void): void {
  const handleRestore = useEffectEvent(onRestore)
  useEffect(() => {
    const handle = (event: PageTransitionEvent) => {
      if (event.persisted) handleRestore()
    }
    window.addEventListener('pageshow', handle)
    return () => window.removeEventListener('pageshow', handle)
  }, [])
}
